import { useCallback, useState, type CSSProperties, type ReactNode } from 'react';

/**
 * Inline result message for a form or panel. Colour comes from an explicit
 * kind set where the outcome is known (try → ok, catch → error) rather than
 * guessing from the wording.
 */
export type FlashKind = 'ok' | 'error' | 'warn';
export interface Flash {
  kind: FlashKind;
  text: ReactNode;
}

export function useFlash() {
  const [flash, setFlash] = useState<Flash | null>(null);
  const ok = useCallback((text: ReactNode) => setFlash({ kind: 'ok', text }), []);
  const error = useCallback((text: ReactNode) => setFlash({ kind: 'error', text }), []);
  const warn = useCallback((text: ReactNode) => setFlash({ kind: 'warn', text }), []);
  const clear = useCallback(() => setFlash(null), []);
  return { flash, setFlash, ok, error, warn, clear };
}

/** Best human message from a thrown value. */
export function errorText(err: unknown, fallback = 'Something went wrong. Please try again.'): string {
  if (err && typeof err === 'object' && 'message' in err) {
    const m = (err as { message?: unknown }).message;
    if (typeof m === 'string' && m.trim()) return m;
  }
  return fallback;
}

const KIND_CLASS: Record<FlashKind, string> = {
  ok: 'text-ok',
  error: 'text-urgent',
  warn: 'text-warning',
};

export function FlashMessage({ flash, style }: { flash: Flash | null; style?: CSSProperties }) {
  if (!flash) return null;
  return (
    <p
      className={KIND_CLASS[flash.kind]}
      role={flash.kind === 'error' ? 'alert' : 'status'}
      style={style}
    >
      {flash.text}
    </p>
  );
}
